/**
 * Freshness, one policy per server resource (web refactor 02, KTD5). Staleness
 * depends on where the server reads from, not on the feature, so entries carry
 * no freshness knobs of their own.
 *
 * Reads: staleTime 0 + refetchOnMount 'always': a page refresh (or re-mount)
 * must re-read the config, never serve the cached copy. This used to be a
 * 5-minute staleTime, which meant a save that raced the backend's replica/memo
 * window left the WRONG value pinned in the dashboard for five more minutes —
 * the admin saw the toggle revert, re-saved, and the loop repeated. The backend
 * side of that bug is fixed for screen features (their admin get-single reads
 * the primary and skips the memo); this keeps the client from re-introducing it
 * on its own. (Carried from `use-screen-feature-config.ts`.)
 */
const READ_POLICY = {
  staleTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: false,
  gcTime: 30 * 60 * 1000
} as const

/**
 * Whether the admin global-features get-single reads the primary IN PRODUCTION.
 *
 * `true` since plan 02 U2b: U2 (SCH-2523, #4348) moved that read to the
 * primary, and every production backend deploy since 2026-10-08 carries it. So
 * a refetch right after a save answers the saved value, and global entries
 * invalidate like screen entries. Set it back to `false` (the `set-cached`
 * policy) only if that read ever goes back to a replica: web and backend
 * deploy separately.
 */
const GLOBAL_READS_PRIMARY = true

/**
 * - `invalidate`: refetch after a save (the server read is read-after-write safe).
 * - `set-cached`: write the locally resolved post-save value into the cache and
 *   do not refetch it.
 */
type AfterSave = 'invalidate' | 'set-cached'

export const freshness: Record<'screen' | 'global', typeof READ_POLICY & { afterSave: AfterSave }> = {
  screen: { ...READ_POLICY, afterSave: 'invalidate' },
  global: { ...READ_POLICY, afterSave: GLOBAL_READS_PRIMARY ? 'invalidate' : 'set-cached' }
}
